import { useEffect, useState } from 'react';
import { CpmButton, CpmCheckbox, CpmTooltip } from '../../../components/design-system';
import {
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
} from '../../../services/reportsAdminService';
import {
    getDailyNotificationSettings,
    updateDailyNotificationSettings,
} from '../../../services/storeManagementService';
import type { DailyNotificationSettings, NotificationReportEntry } from '../../../types/api.types';
import type { FormPhase, StoreBrandReportRow } from '../../../types/reportsAdmin.types';
import CopyIdButton from '../components/CopyIdButton';
import FilteredEmpty from '../components/FilteredEmpty';
import InfoI from '../components/InfoI';
import KindBadge from '../components/KindBadge';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsSkeletonList,
    ReportsWait,
} from '../components/ReportsDialog';
import SearchField from '../components/SearchField';
import { REPORTS_TIPS } from '../copy';
import { plural } from '../helpers';

const ROW_COLS = '24px minmax(0, 1fr) auto 250px';

interface WhatsAppPicksModalProps {
    storeBrandID: string;
    brandName?: string;
    state?: string | null;
    reports: StoreBrandReportRow[];
    onClose: () => void;
    onSaved: (count: number) => void;
}

export default function WhatsAppPicksModal({
    storeBrandID,
    brandName,
    state,
    reports,
    onClose,
    onSaved,
}: WhatsAppPicksModalProps) {
    const onReports = reports.filter((row) => row.enabled);
    const offReports = reports.filter((row) => !row.enabled);
    const [phase, setPhase] = useState<FormPhase>('loading');
    const [settings, setSettings] = useState<DailyNotificationSettings>({
        enabled: false,
        notificationReports: [],
    });
    const [picked, setPicked] = useState<string[]>([]);
    const [savedPicks, setSavedPicks] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [showOff, setShowOff] = useState(false);
    const [error, setError] = useState('');
    const [justSaved, setJustSaved] = useState(false);

    const load = async () => {
        setPhase('loading');
        setError('');
        try {
            const res = await getDailyNotificationSettings(storeBrandID);
            const next = res.data;
            const nextPicked = (next.notificationReports ?? [])
                .map((entry) => entry.reportId)
                .filter((id) => onReports.some((row) => row.reportID === id));
            setSettings(next);
            setPicked(nextPicked);
            setSavedPicks(nextPicked);
            setPhase('idle');
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('fetchError');
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeBrandID]);

    const persist = async () => {
        setPhase('saving');
        setError('');
        setJustSaved(false);
        const existing = new Map(
            (settings.notificationReports ?? []).map((entry) => [entry.reportId, entry])
        );
        const notificationReports: NotificationReportEntry[] = picked.map((id) => {
            const prior = existing.get(id);
            return {
                reportId: id,
                notificationType: prior?.notificationType ?? 'Consolidated Report',
                fileType: prior?.fileType,
            };
        });
        try {
            await updateDailyNotificationSettings(storeBrandID, {
                ...settings,
                notificationReports,
            });
            setSavedPicks(picked);
            setJustSaved(true);
            setPhase('idle');
            onSaved(notificationReports.length);
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const q = search.trim().toLowerCase();
    const matches = (row: StoreBrandReportRow) => !q || row.name.toLowerCase().includes(q);
    const enabledVisible = onReports.filter(matches);
    const offVisible = offReports.filter(matches);
    const noEnabled = onReports.length === 0;
    const filteredEmpty = onReports.length > 0 && enabledVisible.length === 0;
    const sameAsSaved =
        picked.length === savedPicks.length && picked.every((id) => savedPicks.includes(id));

    const togglePick = (id: string) => {
        if (phase === 'saving') return;
        setJustSaved(false);
        setPicked((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    return (
        <ReportsDialog
            open
            width={780}
            zIndex={380}
            onClose={phase === 'saving' ? () => undefined : onClose}
            closeOnBackdrop={phase !== 'saving'}
            closeOnEscape={phase !== 'saving'}
        >
            <ReportsDialogHeader
                kicker="Reports in the daily WhatsApp"
                kickerExtra={
                    <InfoI
                        title={REPORTS_TIPS.DAILY_WHATSAPP.title}
                        body={REPORTS_TIPS.DAILY_WHATSAPP.body}
                    />
                }
                title={
                    brandName
                        ? `Daily WhatsApp notifications — ${brandName}`
                        : 'Daily WhatsApp report picks'
                }
                subtitle="Which reports go out in this store brand’s daily WhatsApp message. Each pick is stored as the catalogue report’s UUID, never the id of the turn-on row it happens to sit on."
                onClose={phase === 'saving' ? undefined : onClose}
            />
            <ReportsDialogBody pad="picker">
                {phase === 'loading' ? (
                    <div>
                        <div style={{ paddingBottom: 16 }}>
                            <ReportsWait label="Getting what is picked for this store brand's daily message. Blank here is not zero picks — zero would be a number." />
                        </div>
                        <ReportsSkeletonList rows={3} />
                    </div>
                ) : null}
                {phase === 'fetchError' ? (
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't load the daily picks"
                        body="The picked set did not come back, so this is not an empty set and not a store brand with nothing on. Nothing was saved, and the drill-in behind this dialog is exactly as it was."
                        reason={reportsAdminErrorReason(error)}
                        action={
                            <CpmButton variant="outline" size="sm" onClick={() => void load()}>
                                Retry the picks
                            </CpmButton>
                        }
                    />
                ) : null}
                {phase !== 'loading' && phase !== 'fetchError' ? (
                    <div>
                        {error ? (
                            <div style={{ marginBottom: 14 }}>
                                <ReportsAlert
                                    tone="danger"
                                    title="Couldn't save the picks"
                                    body="Nothing was written and every tick you made is still here. The saved set is the one that was there before."
                                    reason={reportsAdminErrorReason(
                                        error,
                                        'Nothing was written and every tick you made is still here.'
                                    )}
                                    action={
                                        <CpmButton
                                            variant="outline"
                                            size="sm"
                                            onClick={() => void persist()}
                                        >
                                            Retry the save
                                        </CpmButton>
                                    }
                                />
                            </div>
                        ) : null}
                        {justSaved ? (
                            <div style={{ marginBottom: 14 }}>
                                <ReportsAlert
                                    tone="success"
                                    title={
                                        picked.length === 0
                                            ? 'Saved with 0 picks'
                                            : `Saved ${plural(picked.length, 'pick')}`
                                    }
                                    body={
                                        picked.length === 0
                                            ? 'An empty set saved cleanly — this store brand gets no daily WhatsApp message until something is picked. That is a number, not a failure.'
                                            : `Stored as ${plural(picked.length, 'catalogue UUID')}. Nothing was turned on or off for this store brand, and no template changed.`
                                    }
                                />
                            </div>
                        ) : null}

                        <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                            <SearchField
                                value={search}
                                onChange={setSearch}
                                placeholder="Search this store brand's reports by name"
                                disabled={phase === 'saving'}
                                width={300}
                                size="sm"
                            />
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={phase === 'saving'}
                                onClick={() => setShowOff((v) => !v)}
                            >
                                {showOff
                                    ? 'Hide the reports that are not on'
                                    : 'Also show reports that are not on'}
                            </CpmButton>
                        </div>

                        <div style={{ margin: '14px 0 10px' }}>
                            <div
                                style={{
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    color: 'var(--cpm-text-primary)',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {plural(picked.length, 'report')} picked · {onReports.length} on for
                                this store brand
                            </div>
                            {picked.length === 0 ? (
                                <div
                                    style={{
                                        marginTop: 3,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    0 picks is a number: saving it is a valid empty set, and this
                                    store brand simply gets no daily message.
                                </div>
                            ) : null}
                        </div>

                        {noEnabled ? (
                            <div
                                style={{
                                    padding: 22,
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    background: 'var(--cpm-surface-subtle)',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    This store brand has 0 reports on
                                </div>
                                <div
                                    style={{
                                        marginTop: 5,
                                        fontSize: 12.5,
                                        fontWeight: 500,
                                        lineHeight: 1.5,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    So there is nothing to send daily yet. That is a count, not a
                                    failure — turn a report on in the drill-in behind this dialog
                                    and it becomes pickable here. Saving 0 picks is still allowed.
                                </div>
                            </div>
                        ) : null}

                        {filteredEmpty ? (
                            <FilteredEmpty
                                title="No on report matches that search"
                                cause="The search caused this, not an empty pick set."
                                restated={`The search is “${search.trim()}”. ${plural(onReports.length, 'report')} are on for this store brand.`}
                                onClear={() => setSearch('')}
                            />
                        ) : null}

                        {enabledVisible.length > 0 ? (
                            <div
                                style={{
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    overflow: 'hidden',
                                }}
                            >
                                <SectionHead label="On for this store brand" band />
                                {enabledVisible.map((row) => (
                                    <PickRow
                                        key={row.reportID}
                                        row={row}
                                        picked={picked.includes(row.reportID)}
                                        pickable
                                        disabled={phase === 'saving'}
                                        onToggle={() => togglePick(row.reportID)}
                                    />
                                ))}
                            </div>
                        ) : null}

                        {showOff && offVisible.length > 0 ? (
                            <div
                                style={{
                                    marginTop: 14,
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                    overflow: 'hidden',
                                }}
                            >
                                <SectionHead
                                    label={`${plural(offVisible.length, 'report')} available${state ? ` in ${state}` : ''} but not on for this store brand`}
                                />
                                {offVisible.map((row) => (
                                    <PickRow
                                        key={row.reportID}
                                        row={row}
                                        picked={false}
                                        pickable={false}
                                        disabled
                                        hint="Not on for this store brand"
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </ReportsDialogBody>
            <ReportsDialogFooter
                previewMain={`${plural(picked.length, 'report')} in the daily message`}
                previewSub={
                    sameAsSaved
                        ? 'This is exactly what is saved, so saving again writes the same set — it is allowed and changes nothing.'
                        : 'Saving replaces the picked set for this store brand. Identity is the catalogue UUID, so the turn-on row can be rewritten without losing a pick.'
                }
                saving={phase === 'saving'}
                savingLabel="Saving the daily picks. The write has not finished — do not fire it again, and this dialog stays put until it does."
                onCancel={onClose}
                primaryLabel="Save the picks"
                onPrimary={() => void persist()}
                primaryDisabled={phase === 'loading' || phase === 'fetchError'}
            />
        </ReportsDialog>
    );
}

function SectionHead({ label, band = false }: { label: string; band?: boolean }) {
    return (
        <div
            className="flex items-center justify-between"
            style={{
                gap: 12,
                padding: '9px 16px',
                background: band ? '#EDEDF3' : 'var(--cpm-surface-subtle)',
                borderBottom: band
                    ? '1.5px solid var(--cpm-border-strong)'
                    : '1px solid var(--cpm-border-default)',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: 'var(--cpm-text-secondary)',
            }}
        >
            {label}
        </div>
    );
}

function PickRow({
    row,
    picked,
    pickable,
    disabled,
    hint,
    onToggle,
}: {
    row: StoreBrandReportRow;
    picked: boolean;
    pickable: boolean;
    disabled?: boolean;
    hint?: string;
    onToggle?: () => void;
}) {
    return (
        <div
            className="grid items-center"
            onClick={() => {
                if (pickable) onToggle?.();
            }}
            style={{
                gridTemplateColumns: ROW_COLS,
                gap: 12,
                padding: '9px 16px 9px 14px',
                borderTop: '1px solid var(--cpm-border-default)',
                cursor: pickable ? 'pointer' : 'default',
                opacity: pickable ? 1 : 0.72,
            }}
            onMouseEnter={(e) => {
                if (!pickable) return;
                e.currentTarget.style.background = 'var(--cpm-purple-tint-3)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
            }}
        >
            <div className="flex items-center justify-center">
                {pickable ? (
                    <span onClick={(e) => e.stopPropagation()}>
                        <CpmCheckbox
                            checked={picked}
                            disabled={disabled}
                            onChange={() => onToggle?.()}
                        />
                    </span>
                ) : (
                    <LockIcon />
                )}
            </div>
            <div className="flex items-center min-w-0" style={{ gap: 8 }}>
                <span
                    style={{
                        fontSize: 13.5,
                        fontWeight: 800,
                        letterSpacing: '-0.01em',
                        color: 'var(--cpm-text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {row.name}
                </span>
                {hint ? (
                    <CpmTooltip content={hint} fit>
                        <span
                            className="inline-flex items-center"
                            style={{ color: 'var(--cpm-text-tertiary)' }}
                        >
                            <AlertIcon />
                        </span>
                    </CpmTooltip>
                ) : null}
            </div>
            <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                {row.categories.map((category) => (
                    <span
                        key={category}
                        style={{
                            padding: '3px 8px',
                            borderRadius: 'var(--cpm-radius-6)',
                            background: 'var(--cpm-surface-tag)',
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {category}
                    </span>
                ))}
                {row.kind !== 'GENERATED' ? <KindBadge kind={row.kind} /> : null}
            </div>
            <div className="flex items-center justify-end" style={{ gap: 7 }}>
                <span
                    style={{
                        fontFamily: 'var(--cpm-font-mono)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: 'var(--cpm-text-tertiary)',
                    }}
                >
                    {`${row.reportID.slice(0, 8)}…${row.reportID.slice(-4)}`}
                </span>
                <CopyIdButton id={row.reportID} />
            </div>
        </div>
    );
}

function LockIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cpm-open-icon)"
            strokeWidth="2"
        >
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
    );
}

function AlertIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
        </svg>
    );
}
